import { Dispatch, useCallback, useContext } from "react";
import { unstable_batchedUpdates } from 'react-dom';
import DruidWebchatContext, { DruidWebchatContextType, WebChatState } from "../WebChatContext";
import { buildAuthorizationDto } from '../logics/buildAuthorizationDto';
import { doAuthorize } from "../WebChatServices";
import { setCookie } from "../utils/cookiesUtils";
import pRetry from 'p-retry';
import moment from 'moment';
import { DruidCookies } from "../WebChatOptions";


export const useHandleInitialize = () => {
  const [druidWebChatState, druidWebChatDispatch, getState] = useContext<DruidWebchatContextType>(DruidWebchatContext);

  return useCallback(async () => {
    const backendStartTimestamp = moment().utc();
    const currentState = getState();
    const setCommons = (authorizeResult: { botDto: { contextMenuText: string, contextMenuTranslations: string, avatarLivechatImage?: string }}) => {
      druidWebChatDispatch({
        type: 'SET_INITIALIZED',
        payload: true
      });

      druidWebChatDispatch({
        type: 'INITIALIZATION_STATUS',
        payload: 'success'
      });

      if(authorizeResult?.botDto) {
        const { botDto : { contextMenuText, contextMenuTranslations, avatarLivechatImage } } = authorizeResult;
        if (contextMenuText != null && !!contextMenuText) {
          const contextMenuItems = contextMenuText.split(";").filter(f => f); // not null
          druidWebChatDispatch({
            type: 'SET_CONTEXTMENU',
            payload: contextMenuItems
          });
  
          if (contextMenuTranslations != null && !!contextMenuTranslations) {
            const contextMenuTranslationItems = Object.entries<string>(JSON.parse(contextMenuTranslations || "{}")).reduce((acc, [key, value]) => {
              if (key !== currentState.botLanguages.find(bl => bl.isDefault)?.name) {
                acc[key] = value?.split(";");
              } else {
                acc[key] = contextMenuItems;
              }
              return acc;
            }, {});
            druidWebChatDispatch({
              type: 'SET_CONTEXTMENU_TRANSLATIONS',
              payload: contextMenuTranslationItems
            });
          }
        }

        if(!!avatarLivechatImage) {
          druidWebChatDispatch({
            type: 'SET_MERGEDCHATOPTIONS',
            payload: {
              UI: {
                avatarLivechatImage
              }
            }
          });
        }
      }
    }
    //!druidWebChatState.directlineOptions?.token && !druidWebChatState.directlineInstance -> second condition -> could be botConnection sent from parameters. should stop authorizeCall
    if (druidWebChatState.loaded && currentState.INITIALIZATION_STATUS !== 'failed') {
      if(druidWebChatState.directlineInstance && !druidWebChatState.directlineOptions?.token) {
        const localBackendEndTimestamp = moment().utc();
        const { token, conversationId, webSocket, watermark, druidApiUrl, serverTimestamp, domain, druidBotApiUrl, withCredentials, initiatorUserId, initiatorBotId,speechToken, isBotApiEnabled, isOutOfBandUpload, backendStartTimestamp, backendEndTimestamp, speechRegion, isVoiceChannel, userId, botId,
        } = druidWebChatState.directlineInstance as any;
        const { authorizationResultDto } = currentState.mergedChatOptions;
        druidWebChatDispatch({
          type: 'SET_DIRECTLINEOPTIONS',
          payload: {
            token,
            conversationId,
            webSocket,
            watermark: watermark || '0',
            druidApiUrl,
            druidBotApiUrl,
            serverTimestamp: serverTimestamp.toISOString(),
            isBotApiEnabled: isBotApiEnabled,
            domain,
            isOutOfBandUpload: isOutOfBandUpload,
            backendStartTimestamp: backendStartTimestamp?.toISOString() || localBackendEndTimestamp.toISOString(),
            backendEndTimestamp: backendEndTimestamp?.toISOString(),
            withCredentials: !!withCredentials,
            initiatorUserId: initiatorUserId,
            initiatorBotId: initiatorBotId
          }
        });

        setCommons(authorizationResultDto);
      } else {
        if (!druidWebChatState.directlineOptions?.token) { // common path. above if is used in multichat dependency

          const { authorizationResultDto, withCredentials, withCredentials_authorization, authorizationBaseUrl, baseUrl, authorizationUrl, customAuthHeaders: customAuthHeaders, conversationToken_namePrefix, cookieDomain, conversationTokenEnabled } = currentState.mergedChatOptions;
          const authorizationDto = buildAuthorizationDto(currentState.mergedChatOptions, currentState);
  
          var authorizeResult;
          if (!authorizationResultDto) {
            authorizeResult = await pRetry(async () => {
              const authorizeCallResult = await doAuthorize({
                baseUrl: authorizationBaseUrl || baseUrl,
                authorizationUrl: authorizationUrl,
                authorizationDto,
                withCredentials: (withCredentials_authorization !== undefined ? withCredentials_authorization : withCredentials_authorization) ? "include" : "omit",
                customAuthHeaders
              });
              if (authorizeCallResult.status >= 400 && authorizeCallResult.status < 500) {
                throw new pRetry.AbortError(authorizeCallResult.status?.toString());
              }
  
              return authorizeCallResult.json();
            }, {
              retries: 4,
              minTimeout: 1000,
              maxTimeout: 2000
            }).catch((ex) => {
              druidWebChatDispatch({
                type: 'INITIALIZATION_STATUS',
                payload: 'failed',
                withMessage: ex?.message
              });
            });
  
            // response could be wrapped by APC "result" structure
            authorizeResult = authorizeResult?.result || authorizeResult;
  
            if (!authorizeResult) { // failed
              return;
            } else {
              if (!authorizeResult.token) {
                druidWebChatDispatch({
                  type: 'INITIALIZATION_STATUS',
                  payload: 'failed'
                });
                return;
              }
            }
          } else {
            authorizeResult = authorizationResultDto;
          }
  
  
          const backendEndTimestamp = moment().utc();
  
          const { token, conversationId, conversationToken, watermark, botDto, serverTime, domain, speechToken, speechRegion, isVoiceChannel, userId, botId,
            botDto: { isWebSocket, apiUrl, botApiUrl, isBotApiEnabled, isOutOfBandUpload, logoImage }
          } = authorizeResult;
  
          if (token && conversationToken) {
            setCookie(DruidCookies.DruidConversationToken, conversationToken, { domain: cookieDomain || "" }, currentState);
          }
  
          druidWebChatDispatch({
            type: 'SET_DIRECTLINEOPTIONS',
            payload: {
              token,
              conversationId,
              webSocket: isWebSocket,
              watermark: watermark || '0',
              druidApiUrl: apiUrl,
              druidBotApiUrl: botApiUrl,
              serverTimestamp: serverTime,
              isBotApiEnabled: isBotApiEnabled,
              domain,
              isOutOfBandUpload: isOutOfBandUpload,
              backendStartTimestamp: backendStartTimestamp.toISOString(),
              backendEndTimestamp: backendEndTimestamp.toISOString(),
              withCredentials: !!withCredentials,
              initiatorUserId: userId,
              initiatorBotId: botId
            }
          });
  
          druidWebChatDispatch({
            type: 'SET_SPEECHOPTIONS',
            payload: {
              ...{
                speechToken,
                speechRegion,
                isVoiceChannel
              }
            }
          });
  
          if (logoImage && !currentState.mergedChatOptions.styleOptions?.botAvatarImage) {
            druidWebChatDispatch({
              type: 'SET_MERGEDCHATOPTIONS',
              payload: {
                styleOptions: {
                  botAvatarInitials: '',
                  botAvatarImage: logoImage,
                }
              }
            });
          }
  
          if (userId && userId !== currentState.mergedChatOptions.userId) {// could generate node_modules\botframework-webchat-api\src\hooks\Composer.tsx -> dispatch(disconnect()) on directline
            druidWebChatDispatch({
              type: 'SET_MERGEDCHATOPTIONS',
              payload: {
                UI: {
                  activityStatusUserName: userId // change only the displayName, not the actual userId. could be wrong
                }
              }
            });
          }
  
          setCommons(authorizeResult);
        }
      }
     
    }
  }, [druidWebChatState.directlineOptions, druidWebChatState.mergedChatOptions.baseUrl, druidWebChatState.loaded, druidWebChatState.mergedChatOptions.botId, druidWebChatState.mergedChatOptions.withCredentials, druidWebChatDispatch]);
}